import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchAllSongRequests,
  SongRequest,
  updateSongRequestStatus,
  SongRequestStatus,
  SongRequestPriority,
  approveSongRequest,
  rejectSongRequest,
} from "./songRequestApi";
import styles from "./SongRequestShared.module.css";
import {
  Music,
  Edit,
  Search,
  Clock,
  User,
  Volume2,
  AlertCircle,
  RefreshCw,
  Star,
  CheckCircle,
  XCircle,
  Loader,
  Eye,
} from "lucide-react";

const SongRequestList: React.FC = () => {
  const navigate = useNavigate();
  const [songRequests, setSongRequests] = useState<SongRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<SongRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    loadSongRequests();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredRequests(songRequests);
    } else {
      const filtered = songRequests.filter(
        (request) =>
          request.song_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          request.artist_name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          request.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          request.status?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredRequests(filtered);
    }
  }, [searchTerm, songRequests]);

  const loadSongRequests = async () => {
    try {
      setLoading(true);
      setError("");
      const data: SongRequest[] = await fetchAllSongRequests();
      setSongRequests(data || []);
    } catch (error: any) {
      console.error("Failed to load song requests:", error);
      setError(
        error.response?.data?.message ||
          "Şarkı istekleri yüklenirken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (
    requestId: string,
    newStatus: SongRequestStatus,
    priority?: SongRequestPriority,
    adminNotes?: string
  ) => {
    try {
      setUpdatingId(requestId);

      // API dokümantasyonuna göre approve/reject için ayrı endpoint'ler kullanıyoruz
      if (newStatus === SongRequestStatus.APPROVED) {
        await approveSongRequest(requestId, adminNotes);
      } else if (newStatus === SongRequestStatus.REJECTED) {
        await rejectSongRequest(requestId, adminNotes);
      } else {
        // Diğer durum güncellemeleri için eski metodu kullan
        await updateSongRequestStatus(requestId, newStatus, adminNotes);
      }

      // Update local state
      setSongRequests((prev) =>
        prev.map((request) =>
          request.request_id === requestId
            ? {
                ...request,
                status: newStatus,
                priority: priority || request.priority,
                admin_notes: adminNotes || request.admin_notes,
              }
            : request
        )
      );
    } catch (error: any) {
      console.error("Failed to update status:", error);
      alert(
        "Durum güncellenirken hata oluştu: " +
          (error.message || "Bilinmeyen hata")
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusStats = () => {
    const stats = {
      pending: 0,
      approved: 0,
      rejected: 0,
      total: filteredRequests.length,
    };

    filteredRequests.forEach((request) => {
      if (request.status === "pending") stats.pending++;
      else if (request.status === "approved") stats.approved++;
      else if (request.status === "rejected") stats.rejected++;
    });

    return stats;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return <CheckCircle size={14} style={{ color: "#10b981" }} />;
      case "rejected":
        return <XCircle size={14} style={{ color: "#ef4444" }} />;
      case "pending":
      default:
        return <Clock size={14} style={{ color: "#f59e0b" }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "#10b981";
      case "rejected":
        return "#ef4444";
      case "pending":
      default:
        return "#f59e0b";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "urgent":
        return "#ef4444";
      case "high":
        return "#f59e0b";
      case "normal":
        return "#3b82f6";
      case "low":
      default:
        return "#6b7280";
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <span>Şarkı istekleri yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardBody} style={{ textAlign: "center" }}>
            <AlertCircle
              size={48}
              style={{ color: "#ef4444", marginBottom: "1rem" }}
            />
            <h3 style={{ color: "white", marginBottom: "0.5rem" }}>
              Hata Oluştu
            </h3>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.8)",
                marginBottom: "1.5rem",
              }}
            >
              {error}
            </p>
            <button onClick={loadSongRequests} className={styles.button}>
              <RefreshCw size={16} />
              Tekrar Dene
            </button>
          </div>
        </div>
      </div>
    );
  }

  const statusStats = getStatusStats();

  return (
    <div className={styles.container}>
      {/* Modern Header with Gradient */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <Music size={28} />
            </div>
            <div>
              <h1 className={styles.title}>Şarkı İstekleri</h1>
              <p className={styles.subtitle}>
                Kullanıcı şarkı isteklerini yönetin ve onaylayın
              </p>
            </div>
          </div>

          <div className={styles.headerActions}>
            <button
              onClick={loadSongRequests}
              className={styles.refreshButton}
              title="Yenile"
            >
              <RefreshCw size={18} />
              Yenile
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <Music size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{statusStats.total}</div>
            <div className={styles.statLabel}>Toplam İstek</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div
            className={styles.statIcon}
            style={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            }}
          >
            <Clock size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{statusStats.pending}</div>
            <div className={styles.statLabel}>Beklemede</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div
            className={styles.statIcon}
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            }}
          >
            <CheckCircle size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{statusStats.approved}</div>
            <div className={styles.statLabel}>Onaylandı</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div
            className={styles.statIcon}
            style={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            }}
          >
            <XCircle size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{statusStats.rejected}</div>
            <div className={styles.statLabel}>Reddedildi</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={styles.filtersSection}>
        <div className={styles.searchContainer}>
          <Search size={20} />
          <input
            type="text"
            placeholder="Şarkı, sanatçı veya kullanıcı ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        searchTerm ? (
          <div className={styles.emptyState}>
            <AlertCircle size={48} />
            <h3>Arama sonucu bulunamadı</h3>
            <p>"{searchTerm}" için herhangi bir istek bulunamadı.</p>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Music size={48} />
            <h3>Henüz şarkı isteği yok</h3>
            <p>Kullanıcılar istek gönderdiğinde burada görünecek.</p>
          </div>
        )
      ) : (
        <div className={styles.gridContainer}>
          {filteredRequests.map((request) => (
            <div key={request.request_id} className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.songInfo}>
                  <h3 className={styles.songTitle}>{request.song_name}</h3>
                  <p className={styles.songArtist}>
                    <Volume2 size={14} />
                    {request.artist_name}
                  </p>
                </div>
              </div>

              <div className={styles.cardContent}>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <User size={16} />
                    <span>Kullanıcı</span>
                    <strong>{(request.user_name ?? "").trim() || request.user_email}</strong>
                  </div>

                  <div className={styles.infoItem}>
                    <Clock size={16} />
                    <span>Tarih</span>
                    <strong>{formatDate(request.created_at)}</strong>
                  </div>

                  {request.priority && (
                    <div className={styles.infoItem}>
                      <Star size={16} />
                      <span>Öncelik</span>
                      <strong
                        style={{ color: getPriorityColor(request.priority) }}
                      >
                        {request.priority}
                      </strong>
                    </div>
                  )}
                </div>

                <div className={styles.statusSection}>
                  <div
                    className={styles.statusBadge}
                    style={{
                      backgroundColor: getStatusColor(request.status),
                    }}
                  >
                    {getStatusIcon(request.status)}
                    {request.status}
                  </div>

                  {request.status === "pending" && (
                    <div className={styles.actionButtons}>
                      <button
                        onClick={() =>
                          handleStatusUpdate(
                            request.request_id,
                            SongRequestStatus.APPROVED
                          )
                        }
                        disabled={updatingId === request.request_id}
                        className={styles.approveButton}
                        title="Onayla"
                      >
                        {updatingId === request.request_id ? (
                          <Loader size={16} className={styles.loadingIcon} />
                        ) : (
                          <CheckCircle size={16} />
                        )}
                        Onayla
                      </button>
                      <button
                        onClick={() =>
                          handleStatusUpdate(
                            request.request_id,
                            SongRequestStatus.REJECTED
                          )
                        }
                        disabled={updatingId === request.request_id}
                        className={styles.rejectButton}
                        title="Reddet"
                      >
                        {updatingId === request.request_id ? (
                          <Loader size={16} className={styles.loadingIcon} />
                        ) : (
                          <XCircle size={16} />
                        )}
                        Reddet
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SongRequestList;
